/** Plain words for the failure reasons the engine records. Unknown ones show as is. */
const LABELS: Record<string, string> = {
  otp_timeout: "Code never arrived",
  otp_page_unavailable: "Code page unreachable",
  transport_error: "Couldn't reach the server",
  external_service_connection_failed: "External service connection failed",
  too_many_failures: "Too many failures",
  // Runs stopped before every failure counted, when only these did.
  external_service_failures: "Too many external service connection failures",
  unknown_outcome: "Outcome unclear",
  interrupted: "Interrupted by a restart",
  internal_error: "Internal error",
  has_2fa: "Account has two-step verification",
  product_not_acceptable: "Already registered or blocked",
  product_rejected: "Rejected on review",
  missing_purchase_order: "No open purchase order",
  missing_product_config: "Product not configured",
  account_action_not_allowed: "Seller not allowed to register",
  invalid_api_key: "API key refused",
  api_key_missing: "No API key saved",
  user_inactive: "Seller account inactive",
  session_expired: "Login session expired",
  session_not_found: "Login session lost",
  email_flood: "Email used too recently",
  "telegram:PHONE_NUMBER_BANNED": "Number banned",
  "telegram:PHONE_NUMBER_INVALID": "Invalid number",
  "telegram:PHONE_NUMBER_OCCUPIED": "Already registered",
  "telegram:PHONE_NUMBER_UNOCCUPIED": "No Telegram account",
  "telegram:PHONE_NUMBER_FLOOD": "Too many attempts on this number",
  "telegram:PHONE_CODE_INVALID": "Wrong code",
  "telegram:PHONE_CODE_EXPIRED": "Code expired",
  "telegram:CODE_PAYMENT_REQUIRED": "Telegram wants payment",
  "telegram:AUTH_RESTART": "Telegram restarted the login",
  provider_no_numbers: "The provider had no numbers to sell",
  provider_balance_low: "Not enough balance at the provider",
  provider_key_invalid: "The provider refused the key",
  provider_unavailable: "The provider didn't answer",
  provider_loss_streak: "Too many paid numbers failed in a row",
  provider_account_missing: "The provider account is gone",
  provider_budget_reached: "Spend cap reached",
  provider_operator_not_allowed: "The server doesn't let you pick a number range",
  "checked:already_registered": "Already has an account (checked)",
  "checked:banned": "Banned (checked)",
  "bought:already_used": "Already used in your runs",
  "bought:order_ended": "Number's order ended",
};

const PREFIXES: [string, string][] = [
  ["email_required:", "Email required (auto email off)"],
  ["unsupported_code_type:", "Code not sent by SMS"],
  ["auto_email_failed:", "Auto email failed"],
  ["auto_email_not_available:", "Auto email not offered"],
  ["telegram:", "Telegram error"],
];

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return "";
  const exact = LABELS[reason];
  if (exact) return exact;
  const prefixed = PREFIXES.find(([p]) => reason.startsWith(p));
  return prefixed ? prefixed[1] : reason;
}

/** What to do about a stopped run, by why it stopped. */
const NEXT_STEPS: Record<string, string> = {
  provider_no_numbers: "Continue when it has numbers again.",
  provider_balance_low: "Top up your balance there, then continue.",
  provider_key_invalid: "Update the provider key in Settings, then continue.",
  provider_unavailable: "Continue when it's back.",
  provider_loss_streak: "Look at the failures below, then continue.",
  provider_account_missing: "This run can't buy more numbers. Start a new run.",
  provider_operator_not_allowed: "Start a new run with Any range.",
  too_many_failures: "Fix what caused them, then continue.",
  external_service_failures: "Check the server, then continue.",
  missing_purchase_order: "Open one for this country on the server, then continue.",
  missing_product_config: "Set it up for this country on the server, then continue.",
  invalid_api_key: "Save a working key in Settings, then continue.",
  api_key_missing: "Save one in Settings, then continue.",
  user_inactive: "Continue once it's active again.",
  account_action_not_allowed: "Continue once registering is allowed again.",
  internal_error: "Continue to try again.",
};

export function nextStep(haltReason: string): string {
  return NEXT_STEPS[haltReason] ?? "Fix it, then continue.";
}

/**
 * The failures counted toward a run's stop, most frequent first: "Code never arrived ×6,
 * Auto email failed ×3, and 2 more".
 */
export function failureSummary(reasons: { reason: string; count: number }[], show = 3): string {
  const named = reasons.slice(0, show).map((r) => `${reasonLabel(r.reason)} ×${r.count}`);
  const rest = reasons.length - named.length;
  return rest > 0 ? `${named.join(", ")}, and ${rest} more` : named.join(", ");
}

/** For a failed number: whether "Retry failed" will try it again. */
export function retryClassLabel(cls: string | null | undefined): string {
  return cls === "terminal" ? "Final" : "Can be retried";
}
